import React, { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Paper, Typography, Box, CircularProgress, Alert, Chip } from '@mui/material';
import { TrendingUp, TrendingDown, Remove } from '@mui/icons-material';
import { analyticsApi } from '../../services/api';

interface CategoryComparisonData {
  category_name: string;
  current_month: number;
  previous_month: number;
  change_amount: number;
  change_percentage: number;
  color: string;
}

interface CategoryComparisonResponse {
  data: CategoryComparisonData[];
  current_month_name: string;
  previous_month_name: string;
}

const CategoryComparisonChart: React.FC = () => {
  const [data, setData] = useState<CategoryComparisonResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response: CategoryComparisonResponse = await analyticsApi.getCategoryComparison();
        setData(response);
        setError(null);
      } catch (err) {
        setError('Failed to load category comparison data');
        console.error('Error fetching category comparison data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const getChangeIcon = (percentage: number) => {
    if (percentage > 0) return <TrendingUp fontSize="small" />;
    if (percentage < 0) return <TrendingDown fontSize="small" />;
    return <Remove fontSize="small" />;
  };

  const getChangeColor = (percentage: number) => {
    if (percentage > 0) return '#f44336'; // Red for increase in expenses
    if (percentage < 0) return '#4caf50'; // Green for decrease in expenses
    return '#9e9e9e'; // Gray for no change
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const currentData = payload.find((p: any) => p.dataKey === 'current_month');
      const previousData = payload.find((p: any) => p.dataKey === 'previous_month');
      
      if (currentData && previousData) {
        const changeAmount = currentData.value - previousData.value;
        const changePercentage = previousData.value > 0 
          ? ((changeAmount / previousData.value) * 100).toFixed(1)
          : '0.0';

        return (
          <Box
            sx={{
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              p: 2
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
              {label}
            </Typography>
            <Typography variant="body2">
              {data?.current_month_name}: {formatCurrency(currentData.value)}
            </Typography>
            <Typography variant="body2">
              {data?.previous_month_name}: {formatCurrency(previousData.value)}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: getChangeColor(Number(changePercentage)),
                fontWeight: 'bold',
                mt: 1
              }}
            >
              Change: {formatCurrency(changeAmount)} ({changePercentage}%)
            </Typography>
          </Box>
        );
      }
    }
    return null;
  };

  if (loading) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 400, sm: 450 }, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 400, sm: 450 } }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  if (!data || data.data.length === 0) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 400, sm: 450 } }}>
        <Typography variant="h6" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Category Comparison
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
          <Typography variant="body1" color="text.secondary">
            No category comparison data available
          </Typography>
        </Box>
      </Paper>
    );
  }

  // Filter and sort data - only include categories where either month has spending > 100 CHF
  const filteredData = data.data.filter(item => 
    item.current_month > 100 || item.previous_month > 100
  );
  
  // Sort filtered data by current month spending (descending)
  const sortedData = [...filteredData].sort((a, b) => b.current_month - a.current_month);

  // Handle case where no categories meet the 100 CHF threshold
  if (sortedData.length === 0) {
    return (
      <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 400, sm: 450 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
            Category Comparison
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {data.previous_month_name} vs {data.current_month_name}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300, flexDirection: 'column', gap: 2 }}>
          <Typography variant="body1" color="text.secondary" align="center">
            No significant spending categories (&gt;100 CHF) found
          </Typography>
          <Typography variant="body2" color="text.secondary" align="center">
            All categories had spending below 100 CHF in both months
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, height: { xs: 400, sm: 450 } }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 'bold', fontSize: { xs: '1.1rem', md: '1.25rem' } }}>
          Category Comparison
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {data.previous_month_name} vs {data.current_month_name}
          </Typography>
          <Chip 
            label="≥ 100 CHF" 
            size="small" 
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
        </Box>
      </Box>
      
      <Box sx={{ height: { xs: 200, sm: 240, md: 280 }, mt: 2, px: 1 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart 
            data={sortedData} 
            margin={{ top: 20, right: 20, left: 20, bottom: 60 }}
            barCategoryGap="20%"
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="category_name"
              stroke="#666"
              fontSize={11}
              angle={-45}
              textAnchor="end"
              height={100}
              interval={0}
            />
            <YAxis 
              tickFormatter={formatCurrency}
              stroke="#666"
              fontSize={13}
              width={60}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Bar 
              dataKey="previous_month" 
              fill="#9e9e9e" 
              name={data.previous_month_name}
              radius={[2, 2, 0, 0]}
            />
            <Bar 
              dataKey="current_month" 
              fill="#3f51b5" 
              name={data.current_month_name}
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>

      {/* Change indicators */}
      <Box sx={{ mt: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
          Changes:
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {sortedData.slice(0, 6).map((item) => (
            <Chip
              key={item.category_name}
              size="small"
              icon={getChangeIcon(item.change_percentage)}
              label={`${item.category_name}: ${item.change_percentage >= 0 ? '+' : ''}${item.change_percentage}%`}
              sx={{
                backgroundColor: getChangeColor(item.change_percentage),
                color: 'white',
                '& .MuiChip-icon': {
                  color: 'white'
                }
              }}
            />
          ))}
        </Box>
      </Box>
    </Paper>
  );
};

export default CategoryComparisonChart;